import {useEffect, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {
  getAnonymousId,
  getCurrencyCode,
  getDeviceCategory,
  getUserCountryCode
} from 'modules/meta/selectors'
import {getSearchParameters} from 'modules/sapiSearch/selectors'
import {v4 as uuid} from 'uuid'

import {selectIsAuthenticated} from '@vio/core/src/auth/modules/selectors'
import {roomsToConfigurationObject} from '@vio/core/src/room/business/roomConfiguration'

import {fetchHopperAd} from '../api/hopperAdsApi'
import {HOPPER_DEVICE_TYPE_MAP} from '../constants'

import type {FetchHopperAdParams, HopperAd, HopperAdPlacement} from '../types'

const BANNER_PLACEMENT: HopperAdPlacement = {
  adType: 'HotelInline',
  maximumAdSlots: 1,
  placementId: 'HotelSearchPage'
}

const CAROUSEL_PLACEMENT: HopperAdPlacement = {
  adType: 'DealTile',
  minimumAdSlots: 5,
  maximumAdSlots: 12,
  placementId: 'Homescreen'
}

type UseHopperAdsParams =
  | {variant: 'Banner'; placeId: string | undefined; enabled: boolean}
  | {variant: 'Carousel'; enabled: boolean}

interface UseHopperAdsResult {
  ads: HopperAd[]
  isLoading: boolean
  error: Error | null
}

/**
 * Unified hook to fetch Hopper ads for both the inline banner (SRP) and the
 * homepage carousel.
 *
 * - Banner variant: requires placeId + active search context; fetches a
 *   single HotelInline ad.
 * - Carousel variant: globally targeted; fetches multiple DealTile ads.
 */
export const useHopperAd = (params: UseHopperAdsParams): UseHopperAdsResult => {
  const [ads, setAds] = useState<HopperAd[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const anonymousId = useSelector(getAnonymousId)
  const userCountry = useSelector(getUserCountryCode)
  const deviceCategory = useSelector(getDeviceCategory)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const currency = useSelector(getCurrencyCode)
  const searchParameters = useSelector(getSearchParameters)

  const isBanner = params.variant === 'Banner'
  const placeId = isBanner ? params.placeId : undefined
  const checkIn = isBanner ? searchParameters?.checkIn : undefined
  const checkOut = isBanner ? searchParameters?.checkOut : undefined
  const {numberOfAdults = 0, numberOfChildren = 0} =
    isBanner && searchParameters?.rooms
      ? roomsToConfigurationObject(searchParameters.rooms)
      : {}

  // Banner regenerates pageViewId per search; carousel keeps it stable.
  const pageViewId = useMemo(
    () => uuid(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    isBanner
      ? [placeId, checkIn, checkOut, numberOfAdults, numberOfChildren]
      : []
  )

  const requestParams = useMemo((): FetchHopperAdParams | null => {
    const locale = 'en-US'
    const requestMetadata = {
      channelId: 'vio-web',
      pageViewId,
      sessionId: anonymousId
    }
    const userContext = {currency, locale}

    if (isBanner) {
      if (!placeId || !checkIn || !checkOut) return null

      return {
        placement: BANNER_PLACEMENT,
        requestMetadata,
        targetingParameters: {
          userCountry,
          userDeviceType: HOPPER_DEVICE_TYPE_MAP[deviceCategory],
          userIsSignedIn: isAuthenticated,
          hotelMarketName: placeId,
          searchStartDate: checkIn,
          searchEndDate: checkOut,
          searchNumberOfTravelers: numberOfAdults + numberOfChildren,
          searchIncludesChildren: numberOfChildren > 0
        },
        userContext
      }
    }

    return {
      placement: CAROUSEL_PLACEMENT,
      requestMetadata,
      targetingParameters: {
        userCountry,
        userDeviceType: HOPPER_DEVICE_TYPE_MAP[deviceCategory],
        userIsSignedIn: isAuthenticated,
        marketName: 'global'
      },
      userContext
    }
  }, [
    isBanner,
    placeId,
    checkIn,
    checkOut,
    numberOfAdults,
    numberOfChildren,
    deviceCategory,
    userCountry,
    isAuthenticated,
    currency,
    anonymousId,
    pageViewId
  ])

  const {enabled} = params

  useEffect(() => {
    let cancelled = false
    if (!enabled || !requestParams) {
      // Reset all state — the previous run's cleanup has set cancelled so
      // any in-flight fetch's `finally` will skip its own setIsLoading(false),
      // which would otherwise leave `isLoading` stuck as `true`.
      setAds([])
      setIsLoading(false)
      setError(null)
    } else {
      const loadAds = async () => {
        setIsLoading(true)
        setError(null)
        try {
          const response = await fetchHopperAd(requestParams)
          if (cancelled) return

          setAds(response?.ads?.length ? response.ads : [])
        } catch (err) {
          if (cancelled) return
          setError(err instanceof Error ? err : new Error('Failed to load ads'))
          setAds([])
        } finally {
          if (!cancelled) setIsLoading(false)
        }
      }
      loadAds()
    }

    return () => {
      cancelled = true
    }
  }, [enabled, requestParams])

  return {ads, isLoading, error}
}
